// src/pages/AppCarteraReactTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import moment from "moment";
import { useFilters, useSortBy, usePagination, useBlockLayout } from "react-table";

import ModalCarteraInicial from "../components/ModalCarteraInicial";
import ModalCarteraEdit from "../components/ModalCarteraEdit";

import Table from "../components/TableContainer";
import { getCartera, getCarteraDisponibles } from "../helpers/rutaCarteras";

const Styles = styled.div`
  .table-container { overflow: auto; height: 600px; }

  .table-container table {
    border: 1px solid black;
    border-spacing: 0;
    font-size: 13px;
    table-layout: fixed;
    width: max-content;
  }

  th, td {
    padding: 0.5rem;
    border-bottom: 1px solid black;
    border-right: 1px solid black;
    white-space: nowrap;
  }

  th {
    background-color: #1e58a8;
    color: white;
    position: sticky;
    top: 0;
    z-index: 2;
    text-align: center;
    font-size: 12px;
  }

  td { background-color: white; }

  .pagination {
    padding: 0.5rem;
    background-color: #778899;
    font-size: 15px;
    font-weight: bold;
  }
`;

export default function AppCarteraReactTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [estado, setEstado] = useState("");
  const [tipo, setTipo] = useState("");
  const [soloDisp, setSoloDisp] = useState(false);

  const [showInicial, setShowInicial] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [selected, setSelected] = useState(null);

  const money = useCallback(
    (n) =>
      Number(n || 0).toLocaleString("es-AR", {
        style: "currency",
        currency: "ARS",
      }),
    []
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = soloDisp
        ? await getCarteraDisponibles()
        : await getCartera({
            estado: estado || undefined,
            tipo: tipo || undefined,
          });

      const items = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.items)
        ? resp.items
        : Array.isArray(resp?.elementos)
        ? resp.elementos
        : [];

      setData(items);
    } catch (e) {
      console.error("Error fetch cartera:", e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [estado, tipo, soloDisp]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = useMemo(
    () => [
      {
        Header: "Edit",
        id: "edit",
        width: 60,
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <button
            className="btn btn-link p-0"
            title="Editar"
            onClick={() => {
              setSelected(row.original);
              setShowEdit(true);
            }}
          >
            ✏️
          </button>
        ),
      },

      { Header: "Tipo", accessor: "tipo", width: 120 },
      { Header: "Estado", accessor: "estado", width: 150 },

      {
        Header: "Importe",
        id: "importe",
        accessor: (r) => r.importe || 0,
        width: 250,
        Cell: ({ value }) => <div style={{ textAlign: "right" }}>{money(value)}</div>,
        Footer: ({ rows }) => {
          const t = rows.reduce((s, r) => s + (r.values.importe || 0), 0);
          return (
            <div style={{ textAlign: "right", fontWeight: "bold", color: "red" }}>
              {money(t)}
            </div>
          );
        },
      },

      {
        Header: "Recibido en",
        id: "recibidoEn",
        accessor: (r) => (r.recibidoEn ? new Date(r.recibidoEn) : null),
        width: 140,
        Cell: ({ value }) => (value ? moment(value).format("DD/MM/YYYY") : ""),
      },

      {
        Header: "Fecha Cheque",
        id: "fechaCheque",
        accessor: (r) => {
          const raw = r.fechaCheque ?? r.fechaPagoDiferido;
          if (!raw) return null;

          // acepta ISO y también DD/MM/YYYY
          const m = moment(raw, [moment.ISO_8601, "YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY"], true);
          return m.isValid() ? m.valueOf() : null; // timestamp
        },
        width: 140,
        Cell: ({ value }) => (value ? moment(value).format("DD/MM/YYYY") : ""),
        sortType: (rowA, rowB, id) => {
          const a = rowA.values[id] ?? -Infinity;
          const b = rowB.values[id] ?? -Infinity;
          return a === b ? 0 : a > b ? 1 : -1;
        },
      },

      {
        Header: "Fecha Pago (unif.)",
        id: "fechaPagoUnif",
        accessor: (r) =>
          r.fechaLiquidacion ? new Date(r.fechaLiquidacion) : null,
        width: 150,
        Cell: ({ value }) => (value ? moment(value).format("DD/MM/YYYY") : ""),
      },

      {
        Header: "Nro (unif.)",
        id: "nroUnif",
        accessor: (r) => r.nro || r.nroCheque || r.idICheq || "",
        width: 120,
      },

      {
        Header: "Banco emisor",
        id: "bancoEmisor",
        accessor: (r) => r.bancoEmisor || "",
        width: 160,
      },

      {
        Header: "Cobrado a (unif.)",
        id: "cobradoAUnif",
        accessor: (r) => r.cobradoA || r.librador || r.emisor || "",
        width: 220,
      },

      {
        Header: "CUIT Cobrado a",
        id: "cuitCobradoA",
        accessor: (r) => r.cuitCobradoA || r.cuitLibrador || r.cuitEmisor || "",
        width: 140,
      },

      {
        Header: "Pagado a (unif.)",
        id: "pagadoAUnif",
        accessor: (r) => r.pagadoA || r.beneficiario || "",
        width: 220,
      },

      {
        Header: "CUIT Pagado a",
        id: "cuitPagadoA",
        accessor: (r) => r.cuitPagadoA || r.cuitBeneficiario || "",
        width: 140,
      },

      {
        Header: "Observaciones",
        id: "observaciones",
        accessor: (r) => r.observaciones || "",
        width: 260,
        Cell: ({ value }) => (
          <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
        ),
      },

      {
        Header: "Cliente",
        id: "cliente",
        accessor: (r) => r.clienteId?.razonsocial || r.clienteId?.nombre || "",
        width: 260,
      },

      {
        Header: "Origen",
        id: "origen",
        accessor: (r) => {
          if (r.ocId?.numero) return `OCR ${r.ocId.numero}`;
          if (r.opId?.numero) return `OPR ${r.opId.numero}`;
          return "";
        },
        width: 160,
      },
    ],
    [money]
  );

  return (
    <div className="container mt-4">
      <h3 className="text-center mb-4">CARTERA DE CHEQUES / iCheq</h3>

      <div
        className="mb-3"
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <select
          className="form-select form-select-sm"
          style={{ width: 180 }}
          value={estado}
          onChange={(e) => setEstado(e.target.value)}
        >
          <option value="">Todos los estados</option>
          <option value="EN_CARTERA">EN_CARTERA</option>
          <option value="DEPOSITADO">DEPOSITADO</option>
          <option value="ACREDITADO">ACREDITADO</option>
          <option value="RECHAZADO">RECHAZADO</option>
          <option value="ANULADO">ANULADO</option>
          <option value="EMITIDO">EMITIDO</option>
        </select>

        <select
          className="form-select form-select-sm"
          style={{ width: 160 }}
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
        >
          <option value="">Todos los tipos</option>
          <option value="CHEQUE">CHEQUE</option>
          <option value="ICHEQ">ICHEQ</option>
        </select>

        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="solo-disp"
            checked={soloDisp}
            onChange={(e) => setSoloDisp(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="solo-disp">
            Sólo disponibles (EN_CARTERA)
          </label>
        </div>

        <button className="btn btn-primary btn-sm" onClick={fetchData}>
          Buscar
        </button>

        <button className="btn btn-success btn-sm" onClick={() => setShowInicial(true)}>
          + Saldo inicial
        </button>
      </div>

      <Styles>
        <div className="table-container">
          <Table
            columns={columns}
            data={data}
            loading={loading}
            plugins={[useFilters, useSortBy, usePagination, useBlockLayout]}
            title="CARTERA DE CHEQUES / iCheq"
            filenamePrefix="Cartera"
            pdfProfile="simple"
          />
        </div>
      </Styles>

      <ModalCarteraInicial
        show={showInicial}
        onClose={() => setShowInicial(false)}
        onSaved={fetchData}
      />

      <ModalCarteraEdit
        show={showEdit}
        item={selected}
        onClose={() => {
          setShowEdit(false);
          setSelected(null);
        }}
        onSaved={() => {
          setShowEdit(false);
          setSelected(null);
          fetchData();
        }}
      />
    </div>
  );
}
